import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Chip,
} from "@mui/material";
import { RADIUS_SMALL } from "../../styles/standaloneLayoutTokens";

/** Split internal `to` into pathname and search (for active-state matching). */
function parseInternalLinkPathSearch(link) {
  if (typeof link !== "string") return { pathname: "", search: "" };
  const q = link.indexOf("?");
  if (q === -1) return { pathname: link, search: "" };
  return { pathname: link.slice(0, q), search: `?${link.slice(q + 1)}` };
}

const NavListItem = ({
  icon,
  text,
  secondary,
  link,
  onClick,
  disabled,
  noSelect,
  collapsed = false,
  comingSoon = false,
  /** Shown after primary text (e.g. status chip). */
  endAdornment,
  /** Tooltip when drawer is collapsed; defaults to `text` if it is a string. */
  tooltipTitle,
  sx,
  /** When set, icon + text use this color; current route match uses bold primary (CareMap main menu). */
  itemColor,
  /** When true (path-only links), treat `/base` and `/base/...` as active. Ignored when link has a query string. */
  matchPathPrefix = false,
  /** Extra sx merged into ListItemButton (e.g. LOTSOG drawer active navy/gold). */
  listItemButtonSx,
  /** Applied to ListItemButton for Playwright / e2e selectors. */
  buttonTestId,
}) => {
  const location = useLocation();
  let external = false;
  if (link) {
    const pattern = /^((http|https):\/\/)/;
    external = pattern.test(link);
  }

  let routeMatchesLink = false;
  if (link && !external) {
    const { pathname: toPath, search: toSearch } = parseInternalLinkPathSearch(link);
    const searchMatches = toSearch === "" || location.search === toSearch;
    if (!searchMatches) {
      routeMatchesLink = false;
    } else if (toSearch !== "") {
      routeMatchesLink = location.pathname === toPath;
    } else if (matchPathPrefix && toPath !== "/") {
      routeMatchesLink =
        location.pathname === toPath || location.pathname.startsWith(`${toPath}/`);
    } else {
      routeMatchesLink = location.pathname === toPath;
    }
  }

  const isSelected = noSelect ? false : routeMatchesLink;

  const listItemIconSx = {
    minWidth: collapsed ? 0 : 56,
    justifyContent: "center",
    opacity: comingSoon ? 0.6 : 1,
    ...(itemColor && { color: itemColor }),
  };

  const listItemTextSx = {
    ...(comingSoon ? { opacity: 0.6 } : {}),
    ...(itemColor && {
      "& .MuiListItemText-primary": {
        color: itemColor,
        fontWeight: isSelected ? 700 : 400,
      },
      "& .MuiListItemText-secondary": {
        color: itemColor,
        opacity: 0.72,
        fontWeight: 400,
      },
    }),
  };

  const inner = (
    <>
      <ListItemIcon sx={listItemIconSx}>{icon}</ListItemIcon>
      {!collapsed && (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1, minWidth: 0 }}>
          <ListItemText
            primary={text}
            secondary={secondary}
            sx={{
              ...(Object.keys(listItemTextSx).length ? listItemTextSx : comingSoon ? { opacity: 0.6 } : {}),
              flex: '1 1 auto',
              minWidth: 0,
            }}
          />
          {endAdornment}
          {comingSoon && (
            <Chip
              label="Coming Soon"
              size="small"
              sx={{
                fontSize: '0.65rem',
                height: 20,
                bgcolor: 'warning.light',
                color: 'warning.contrastText',
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      )}
    </>
  );

  const listItemContent = (
    <ListItem 
      disablePadding 
      onClick={onClick} 
      disabled={disabled}
      sx={sx}
    >
      {onClick || link ? (
        <ListItemButton
          selected={isSelected}
          data-testid={buttonTestId}
          sx={{
            justifyContent: collapsed ? "center" : "flex-start",
            px: collapsed ? 2 : 2,
            borderRadius: RADIUS_SMALL,
            ...(itemColor && {
              "&.Mui-selected": {
                backgroundColor: "rgba(27, 69, 132, 0.08)",
              },
              "&.Mui-selected:hover": {
                backgroundColor: "rgba(27, 69, 132, 0.12)",
              },
            }),
            ...listItemButtonSx,
          }}
        >
          {inner}
        </ListItemButton>
      ) : (
        inner
      )}
    </ListItem>
  );

  const defaultTooltip =
    typeof text === "string" ? text : "";
  const resolvedTooltip = tooltipTitle ?? defaultTooltip;

  // Wrap in tooltip when collapsed
  const item = collapsed ? (
    <Tooltip title={resolvedTooltip || ""} placement="right" arrow>
      {listItemContent}
    </Tooltip>
  ) : (
    listItemContent
  );

  // Internal link
  if (link && !external) return <Link to={link}>{item}</Link>;

  // External link
  if (link && external)
    return (
      <Link to={link} target="_blank" rel="noreferrer">
        {item}
      </Link>
    );

  return item;
};

export default NavListItem;
